/**
 * Discord channels form (§4.4.3).
 *
 * Handles:
 *   - masked bot token with show/hide toggle
 *   - "Test connection" → calls channelsApi.validateDiscord
 *   - user allowlist as a chip input
 *   - channel picker populated from validated guild list
 *   - DM policy selector
 *
 * Parent wires the Apply button to a PATCH agent call + step-up.
 * Uses native <select> / checkboxes to avoid adding Radix deps.
 */
import { useEffect, useState } from 'react';
import { Eye, EyeOff, CheckCircle2, XCircle, X as XIcon, Loader2 } from 'lucide-react';
import type { AgentConfig, DmPolicy, UpdateAgentPayload } from '@/api/agents';
import { channelsApi, type DiscordGuild } from '@/api/channels';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface ChannelsFormProps {
  agent: AgentConfig;
  onReview: (patch: UpdateAgentPayload, version: string) => void;
}

interface ValidationState {
  status: 'idle' | 'validating' | 'ok' | 'error';
  botName?: string;
  error?: string;
}

const selectClass = cn(
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1',
  'text-sm transition-colors focus-visible:outline-none focus-visible:ring-2',
  'focus-visible:ring-ring focus-visible:ring-offset-2'
);

export function ChannelsForm({ agent, onReview }: ChannelsFormProps) {
  const discord = agent.discord;
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [validation, setValidation] = useState<ValidationState>({ status: 'idle' });
  const [guilds, setGuilds] = useState<DiscordGuild[]>([]);
  const [channelIds, setChannelIds] = useState<string[]>(discord?.channel_ids ?? []);
  const [allowlist, setAllowlist] = useState<string[]>(discord?.user_id_allowlist ?? []);
  const [newUserId, setNewUserId] = useState('');
  const [dmPolicy, setDmPolicy] = useState<DmPolicy>(discord?.dm_policy ?? 'allowlist');

  useEffect(() => {
    setChannelIds(discord?.channel_ids ?? []);
    setAllowlist(discord?.user_id_allowlist ?? []);
    setDmPolicy(discord?.dm_policy ?? 'allowlist');
  }, [discord]);

  async function handleTest() {
    if (!token) return;
    setValidation({ status: 'validating' });
    try {
      const res = await channelsApi.validateDiscord(token);
      if (!res.valid) {
        setValidation({ status: 'error', error: res.error ?? 'Invalid token' });
        return;
      }
      setValidation({ status: 'ok', botName: res.bot_name });
      try {
        const g = await channelsApi.listDiscordGuilds(token);
        setGuilds(g.guilds);
      } catch {
        /* best-effort — we can still save without a guild list */
      }
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to contact Discord';
      setValidation({ status: 'error', error: message });
    }
  }

  function toggleChannel(id: string) {
    setChannelIds((cur) =>
      cur.includes(id) ? cur.filter((c) => c !== id) : [...cur, id]
    );
  }

  function addUserId() {
    const trimmed = newUserId.trim();
    if (!trimmed) return;
    if (!/^\d{15,25}$/.test(trimmed)) return; // reject non-snowflakes silently
    if (!allowlist.includes(trimmed)) {
      setAllowlist((cur) => [...cur, trimmed]);
    }
    setNewUserId('');
  }

  function removeUserId(id: string) {
    setAllowlist((cur) => cur.filter((u) => u !== id));
  }

  function handleSubmit() {
    const patch: UpdateAgentPayload = {
      discord: {
        user_id_allowlist: allowlist,
        channel_ids: channelIds,
        dm_policy: dmPolicy,
      },
    };
    if (token) {
      patch.discord!.bot_token = token;
    }
    onReview(patch, agent.version ?? '');
  }

  const currentTokenMasked = discord?.bot_token_masked ?? '';

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="space-y-2">
        <Label htmlFor="bot_token">Bot token</Label>
        {currentTokenMasked && !token && (
          <p className="text-xs text-muted-foreground">
            Current: <code>{currentTokenMasked}</code> — leave blank to keep unchanged.
          </p>
        )}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input
              id="bot_token"
              type={showToken ? 'text' : 'password'}
              value={token}
              onChange={(e) => {
                setToken(e.target.value);
                setValidation({ status: 'idle' });
              }}
              placeholder="Paste a new Discord bot token"
              autoComplete="off"
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => setShowToken((v) => !v)}
              aria-label={showToken ? 'Hide token' : 'Show token'}
            >
              {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleTest}
            disabled={!token || validation.status === 'validating'}
          >
            {validation.status === 'validating' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Testing…
              </>
            ) : (
              'Test connection'
            )}
          </Button>
        </div>
        {validation.status === 'ok' && (
          <p className="flex items-center gap-1 text-sm text-success">
            <CheckCircle2 className="h-4 w-4" />
            Connected as <strong>{validation.botName}</strong>
          </p>
        )}
        {validation.status === 'error' && (
          <p className="flex items-center gap-1 text-sm text-destructive" role="alert">
            <XCircle className="h-4 w-4" />
            {validation.error}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label>User ID allowlist</Label>
        <p className="text-xs text-muted-foreground">
          Only users with these Discord IDs can message the agent (unless DM policy overrides).
        </p>
        <div className="flex flex-wrap gap-2">
          {allowlist.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded bg-secondary px-2 py-0.5 text-xs"
            >
              {id}
              <button
                type="button"
                onClick={() => removeUserId(id)}
                aria-label={`Remove ${id}`}
                className="hover:text-destructive"
              >
                <XIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
          {allowlist.length === 0 && (
            <span className="text-xs italic text-muted-foreground">Nobody yet</span>
          )}
        </div>
        <div className="flex gap-2">
          <Input
            value={newUserId}
            onChange={(e) => setNewUserId(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addUserId();
              }
            }}
            placeholder="Discord user ID (snowflake)"
            aria-label="New user ID"
          />
          <Button type="button" variant="outline" onClick={addUserId} disabled={!newUserId.trim()}>
            Add
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Channels</Label>
        {guilds.length === 0 ? (
          <div className="rounded-md border border-border p-3 text-sm text-muted-foreground">
            {channelIds.length > 0 ? (
              <>
                Current channel IDs:{' '}
                {channelIds.map((c) => (
                  <code key={c} className="mx-1">
                    {c}
                  </code>
                ))}
                <p className="mt-2">Test the token above to pick channels from a list.</p>
              </>
            ) : (
              <>Test the token above to populate the channel list.</>
            )}
          </div>
        ) : (
          <div className="max-h-64 space-y-3 overflow-auto rounded-md border border-border p-3">
            {guilds.map((g) => (
              <div key={g.id}>
                <div className="mb-1 text-xs font-semibold text-muted-foreground">{g.name}</div>
                <ul className="space-y-1 pl-2">
                  {g.channels
                    .filter((c) => c.type === 'text')
                    .map((c) => (
                      <li key={c.id}>
                        <label className="flex cursor-pointer items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={channelIds.includes(c.id)}
                            onChange={() => toggleChannel(c.id)}
                            className="accent-primary"
                          />
                          <span># {c.name}</span>
                        </label>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="dm_policy">DM policy</Label>
        <select
          id="dm_policy"
          className={selectClass}
          value={dmPolicy}
          onChange={(e) => setDmPolicy(e.target.value as DmPolicy)}
        >
          <option value="allowlist">Allowlist only (respect user IDs above)</option>
          <option value="pairing">Pairing mode (DMs allowed for pairing)</option>
          <option value="disabled">Disabled (no DMs)</option>
        </select>
      </div>

      <div className="flex justify-end">
        <Button type="button" onClick={handleSubmit}>
          Review changes
        </Button>
      </div>
    </div>
  );
}
